import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, Eye, Columns2, Map } from "lucide-react";
import { IrisUploader } from "./IrisUploader";
import { IrisCanvas } from "./IrisCanvas";
import { ZoneInfoPanel } from "./ZoneInfoPanel";
import { ZoneObservationForm } from "./ZoneObservationForm";
import { ManualAdjuster } from "./ManualAdjuster";
import { SelectedZonesPanel, SelectedZoneEntry } from "./SelectedZonesPanel";
import { IrisSvgMapV2, ZoneDataV2 } from "./IrisSvgMapV2";
import { detectIrisWithAI, detectIrisClientSide } from "@/services/irisDetectionService";
import { toast } from "sonner";
import { IrisZone, IrisCalibration, ZonesConfig } from "./IrisInteractiveMap";
import { supabase } from "@/integrations/supabase/client";
import type { IrisPhoto } from "@/types/iridology";

interface EyeData {
  imageData: string | null;
  calibration: IrisCalibration | null;
  imageDimensions: { width: number; height: number };
  isDetecting: boolean;
  isManualMode: boolean;
}

interface ZoneObservation {
  id: string;
  zone_id: string;
  zone_label: string;
  zone_ring: string;
  eye_side: string;
  observation_notes: string | null;
  severity_level: string | null;
  is_highlighted: boolean;
}

interface IrisComparisonViewProps {
  className?: string;
  sessionId?: string;
  patientName?: string;
  sessionPhotos?: IrisPhoto[];
  onSelectedZonesChange?: (zones: SelectedZoneEntry[]) => void;
  selectedZonesForExport?: SelectedZoneEntry[];
  rightEyeData?: { imageData: string | null; calibration: IrisCalibration | null };
  leftEyeData?: { imageData: string | null; calibration: IrisCalibration | null };
  onEyeDataChange?: (rightEye: EyeData, leftEye: EyeData) => void;
  onPhotoSaved?: (photo: any) => void;
}

export const IrisComparisonView: React.FC<IrisComparisonViewProps> = ({
  className = "",
  sessionId,
  patientName = "Patient",
  sessionPhotos,
  onSelectedZonesChange,
  selectedZonesForExport: externalSelectedZones,
  onEyeDataChange,
  onPhotoSaved,
}) => {
  const [leftEye, setLeftEye] = useState<EyeData>({
    imageData: null,
    calibration: null,
    imageDimensions: { width: 0, height: 0 },
    isDetecting: false,
    isManualMode: false,
  });
  const [rightEye, setRightEye] = useState<EyeData>({
    imageData: null,
    calibration: null,
    imageDimensions: { width: 0, height: 0 },
    isDetecting: false,
    isManualMode: false,
  });

  const [zonesConfigD, setZonesConfigD] = useState<ZonesConfig | null>(null);
  const [zonesConfigG, setZonesConfigG] = useState<ZonesConfig | null>(null);
  const [selectedZone, setSelectedZone] = useState<IrisZone | null>(null);
  const [selectedEye, setSelectedEye] = useState<"D" | "G" | null>(null);
  const [showObservationForm, setShowObservationForm] = useState(false);
  const [observations, setObservations] = useState<ZoneObservation[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [internalSelectedZones, setInternalSelectedZones] = useState<SelectedZoneEntry[]>([]);
  const [showCartographyRight, setShowCartographyRight] = useState(true); // Toggle carte oeil droit
  const [showCartographyLeft, setShowCartographyLeft] = useState(true); // Toggle carte oeil gauche
  const [viewMode] = useState<"svg">("svg"); // Only carte iridologique mode now

  // Use external or internal state
  const selectedZonesForExport = externalSelectedZones ?? internalSelectedZones;
  const setSelectedZonesForExport = onSelectedZonesChange ?? setInternalSelectedZones;

  // Notify parent of eye data changes
  useEffect(() => {
    onEyeDataChange?.(rightEye, leftEye);
  }, [rightEye, leftEye, onEyeDataChange]);


  const loadObservations = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data, error } = await supabase
        .from("iridology_zone_observations")
        .select("*")
        .eq("session_id", sessionId);
      
      if (error) throw error;
      setObservations(data || []);
    } catch (error) {
      console.error("Error loading observations:", error);
    }
  }, [sessionId]);


  useEffect(() => {
    loadObservations();
  }, [loadObservations]);

  // Load both zone configs - use complete JSON files with all zones
  useEffect(() => {
    const loadZones = async () => {
      try {
        const [respD, respG] = await Promise.all([
          fetch("/iridology/zones_iris_droit_complete.json"),
          fetch("/iridology/zones_iris_gauche_complete.json"),
        ]);
        if (respD.ok) {
          const dataD = await respD.json();
          setZonesConfigD({
            eye: "D",
            eyeLabel: "Œil Droit",
            description: "Cartographie complète de l'œil droit avec toutes les zones iridologiques",
            zones: dataD.map((z: any) => ({
              id: z.id,
              label: z.label,
              ring: "organic" as const,
              startAngle: 0,
              endAngle: 360,
              priority: 1,
              color: "#4CAF50",
              subZones: [],
              description_iridologie: z.lecture_iridologique || "",
              pistes_action: z.pistes_accompagnement || [],
              signaux_alerte: z.points_vigilance || [],
            })),
            innerRing: []
          });
        }
        if (respG.ok) {
          const dataG = await respG.json();
          setZonesConfigG({
            eye: "G",
            eyeLabel: "Œil Gauche",
            description: "Cartographie complète de l'œil gauche avec toutes les zones iridologiques",
            zones: dataG.map((z: any) => ({
              id: z.id,
              label: z.label,
              ring: "organic" as const,
              startAngle: 0,
              endAngle: 360,
              priority: 1,
              color: "#4CAF50",
              subZones: [],
              description_iridologie: z.lecture_iridologique || "",
              pistes_action: z.pistes_accompagnement || [],
              signaux_alerte: z.points_vigilance || [],
            })),
            innerRing: []
          });
        }
      } catch (error) {
        console.error("Error loading zones:", error);
      }
    };
    loadZones();
  }, []);

  const getAllZones = (config: ZonesConfig | null) => {
    if (!config) return [];
    // Support both old format (zones/innerRing) and new format (zones/innerRing/peripheralRing)
    const outer = (config.zones || []).map((z) => ({ ...z, ring: z.ring || ("outer" as const) }));
    const inner = (config.innerRing || []).map((z) => ({ ...z, ring: z.ring || ("inner" as const) }));
    const peripheral = ((config as any).peripheralRing || []).map((z: any) => ({ ...z, ring: z.ring || ("peripheral" as const) }));
    return [...outer, ...inner, ...peripheral];
  };

  const performDetection = useCallback(
    async (
      imageData: string,
      dimensions: { width: number; height: number },
      eyeSide: "D" | "G",
      setEyeData: React.Dispatch<React.SetStateAction<EyeData>>
    ) => {
      setEyeData((prev) => ({ ...prev, isDetecting: true }));

      try {
        let result = await detectIrisWithAI(imageData, eyeSide, dimensions.width, dimensions.height);

        if (result.confidence < 0.5) {
          const clientResult = await detectIrisClientSide(imageData);
          if (clientResult.confidence > result.confidence) {
            result = clientResult;
          }
        }

        setEyeData((prev) => ({
          ...prev,
          calibration: {
            centerX: result.centerX,
            centerY: result.centerY,
            pupilRadius: result.pupilRadius,
            irisRadius: result.irisRadius,
            confidence: result.confidence,
          },
          isDetecting: false,
          isManualMode: result.confidence < 0.6,
        }));

        if (result.confidence < 0.6) {
          toast.info(`Œil ${eyeSide === "D" ? "droit" : "gauche"}: ajustez manuellement si nécessaire`);
        }
      } catch (error) {
        console.error("Detection error:", error);
        setEyeData((prev) => ({
          ...prev,
          calibration: {
            centerX: dimensions.width / 2,
            centerY: dimensions.height / 2,
            pupilRadius: Math.min(dimensions.width, dimensions.height) * 0.08,
            irisRadius: Math.min(dimensions.width, dimensions.height) * 0.3,
            confidence: 0,
          },
          isDetecting: false,
          isManualMode: true,
        }));
      }
    },
    []
  );


  // Hydrate comparison tab from previously saved "Comparaison" photos
  const hydratedRef = useRef<{ D: boolean; G: boolean }>({ D: false, G: false });

  const urlToDataUrlWithDimensions = useCallback(async (url: string) => {
    const res = await fetch(url);
    const blob = await res.blob();

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });

    const img = new Image();
    img.src = dataUrl;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Image load failed"));
    });

    return { dataUrl, dimensions: { width: img.naturalWidth, height: img.naturalHeight } };
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    if (!sessionPhotos || sessionPhotos.length === 0) return;

    // Sort all photos by date (newest first)
    const sortedPhotos = [...sessionPhotos].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    // Prefer photos tagged "comparaison", fallback to any photo for that eye
    const findBestPhoto = (eyeSide: string) => {
      const tagged = sortedPhotos.find(
        (p) => p.eye_side === eyeSide && (p.notes || "").trim().toLowerCase() === "comparaison"
      );
      return tagged || sortedPhotos.find((p) => p.eye_side === eyeSide);
    };

    const latestRight = findBestPhoto("right");
    const latestLeft = findBestPhoto("left");

    const hydrateEye = async (eyeSide: "D" | "G", photoUrl: string) => {
      const setEyeData = eyeSide === "D" ? setRightEye : setLeftEye;
      const { dataUrl, dimensions } = await urlToDataUrlWithDimensions(photoUrl);

      setEyeData((prev) => ({
        ...prev,
        imageData: dataUrl,
        imageDimensions: dimensions,
        isManualMode: false,
      }));

      await performDetection(dataUrl, dimensions, eyeSide, setEyeData);
    };

    const tasks: Promise<void>[] = [];

    if (!hydratedRef.current.D && !rightEye.imageData && latestRight?.photo_url) {
      hydratedRef.current.D = true;
      tasks.push(hydrateEye("D", latestRight.photo_url));
    }

    if (!hydratedRef.current.G && !leftEye.imageData && latestLeft?.photo_url) {
      hydratedRef.current.G = true;
      tasks.push(hydrateEye("G", latestLeft.photo_url));
    }

    if (tasks.length) {
      void Promise.allSettled(tasks);
    }
  }, [sessionId, sessionPhotos, rightEye.imageData, leftEye.imageData, performDetection, urlToDataUrlWithDimensions]);

  const persistComparisonPhoto = useCallback(
    async (eyeSide: "D" | "G", imageDataUrl: string) => {
      if (!sessionId) return;

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Non authentifié");

        const dbEyeSide = eyeSide === "D" ? "right" : "left";

        const blob = await (await fetch(imageDataUrl)).blob();
        const fileName = `${user.id}/${sessionId}/comparison_${dbEyeSide}_${Date.now()}.jpg`;

        const { error: uploadError } = await supabase.storage
          .from("iris-photos")
          .upload(fileName, blob, { contentType: "image/jpeg", upsert: true });
        if (uploadError) throw uploadError;

        const { data: signedUrlData, error: signedUrlError } = await supabase.storage
          .from("iris-photos")
          .createSignedUrl(fileName, 60 * 60 * 24 * 365);
        if (signedUrlError) throw signedUrlError;

        const { count, error: countError } = await supabase
          .from("iris_photos")
          .select("id", { count: "exact", head: true })
          .eq("session_id", sessionId)
          .eq("eye_side", dbEyeSide);
        if (countError) throw countError;

        const { data: row, error: insertError } = await supabase
          .from("iris_photos")
          .insert({
            session_id: sessionId,
            user_id: user.id,
            eye_side: dbEyeSide,
            photo_url: signedUrlData.signedUrl,
            photo_order: (count ?? 0) + 1,
            notes: "Comparaison",
          })
          .select()
          .single();
        if (insertError) throw insertError;

        onPhotoSaved?.(row);
        toast.success(`Photo Comparaison enregistrée (${dbEyeSide === "right" ? "œil droit" : "œil gauche"})`);
      } catch (error) {
        console.error("Erreur sauvegarde photo comparaison:", error);
        toast.error("Impossible d'enregistrer la photo (Comparaison)");
      }
    },
    [sessionId, onPhotoSaved]
  );

  const handleImageLoad = (
    eyeSide: "D" | "G",
    data: string,
    dimensions: { width: number; height: number }
  ) => {
    const setEyeData = eyeSide === "D" ? setRightEye : setLeftEye;
    setEyeData((prev) => ({
      ...prev,
      imageData: data,
      imageDimensions: dimensions,
      isManualMode: false,
    }));
    performDetection(data, dimensions, eyeSide, setEyeData);

    // Auto-save comparison photos so they appear in the gallery and PDF
    void persistComparisonPhoto(eyeSide, data);
  };

  const handleZoneClick = (zone: IrisZone | null, eye: "D" | "G") => {
    setSelectedZone(zone);
    setSelectedEye(zone ? eye : null);
    setShowObservationForm(false);
  };

  const handleAddObservation = () => {
    setShowObservationForm(true);
  };

  const handleObservationSaved = () => {
    setShowObservationForm(false);
    loadObservations();
  };

  const handleAddToSelection = () => {
    if (!selectedZone || !selectedEye) return;
    
    const exists = selectedZonesForExport.some(
      (z) => z.zone.id === selectedZone.id && z.eyeSide === selectedEye
    );
    
    if (exists) {
      setSelectedZonesForExport(
        selectedZonesForExport.filter((z) => !(z.zone.id === selectedZone.id && z.eyeSide === selectedEye))
      );
      toast.info("Zone retirée de la sélection");
    } else {
      setSelectedZonesForExport([...selectedZonesForExport, { zone: selectedZone, eyeSide: selectedEye, severityLevel: "normal" }]);
      toast.success("Zone ajoutée à la sélection");
    }
  };

  const isZoneInSelection = (zoneId: string, eyeSide: "D" | "G") => {
    return selectedZonesForExport.some(
      (z) => z.zone.id === zoneId && z.eyeSide === eyeSide
    );
  };

  const handleRemoveZoneFromSelection = (zoneId: string, zoneSide: "D" | "G") => {
    setSelectedZonesForExport(
      selectedZonesForExport.filter((z) => !(z.zone.id === zoneId && z.eyeSide === zoneSide))
    );
  };

  const handleUpdateZone = (zoneId: string, zoneSide: "D" | "G", updates: Partial<SelectedZoneEntry>) => {
    setSelectedZonesForExport(
      selectedZonesForExport.map((z) =>
        z.zone.id === zoneId && z.eyeSide === zoneSide ? { ...z, ...updates } : z
      )
    );
  };

  const handleClearAllSelection = () => {
    setSelectedZonesForExport([]);
  };

  const handleClearEye = (eyeSide: "D" | "G") => {
    const setEyeData = eyeSide === "D" ? setRightEye : setLeftEye;
    setEyeData({
      imageData: null,
      calibration: null,
      imageDimensions: { width: 0, height: 0 },
      isDetecting: false,
      isManualMode: false,
    });
  };

  const handleCalibrationChange = (eyeSide: "D" | "G", newCalibration: Partial<IrisCalibration>) => {
    const setEyeData = eyeSide === "D" ? setRightEye : setLeftEye;
    setEyeData((prev) => ({
      ...prev,
      calibration: prev.calibration ? { ...prev.calibration, ...newCalibration } : null,
    }));
  };

  // Get eye data for parent component
  const getRightEyeExportData = () => ({
    imageData: rightEye.imageData,
    calibration: rightEye.calibration,
  });

  const getLeftEyeExportData = () => ({
    imageData: leftEye.imageData,
    calibration: leftEye.calibration,
  });

  const EyePanel = ({
    eyeSide,
    eyeData,
    zonesConfig,
    label,
  }: {
    eyeSide: "D" | "G";
    eyeData: EyeData;
    zonesConfig: ZonesConfig | null;
    label: string;
  }) => {
    const allZones = getAllZones(zonesConfig);
    const setEyeData = eyeSide === "D" ? setRightEye : setLeftEye;

    return (
      <div className="flex-1 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-primary" />
            <span className="font-medium">{label}</span>
            {eyeData.calibration && (
              <Badge variant="outline" className="text-xs">
                Confiance: {Math.round(eyeData.calibration.confidence * 100)}%
              </Badge>
            )}
          </div>
        </div>

        {!eyeData.imageData ? (
          <div className="relative isolate border-2 border-dashed border-muted-foreground/30 rounded-lg p-4 min-h-[200px] flex items-center justify-center pointer-events-auto">
            <IrisUploader
              onImageLoad={(data, dimensions) => handleImageLoad(eyeSide, data, dimensions)}
              onEyeSideChange={() => {}}
              eyeSide={eyeSide}
              hasImage={false}
              onClear={() => handleClearEye(eyeSide)}
              compact={true}
            />
          </div>
        ) : (
          <div className="space-y-2">
            <IrisCanvas
              imageData={eyeData.imageData}
              calibration={eyeData.calibration!}
              zones={allZones}
              onZoneClick={(zone) => handleZoneClick(zone, eyeSide)}
              selectedZone={selectedEye === eyeSide ? selectedZone : null}
              isDetecting={eyeData.isDetecting}
            />

            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleClearEye(eyeSide)}
              >
                Changer l'image
              </Button>
              {!eyeData.isManualMode && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEyeData((prev) => ({ ...prev, isManualMode: true }))}
                >
                  Ajuster
                </Button>
              )}
            </div>

            {eyeData.isManualMode && eyeData.calibration && (
              <ManualAdjuster
                calibration={eyeData.calibration}
                imageDimensions={eyeData.imageDimensions}
                onChange={(newCal) => handleCalibrationChange(eyeSide, newCal)}
                onValidate={() => setEyeData((prev) => ({ ...prev, isManualMode: false }))}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const getExistingObservation = (zoneId: string, eyeSide: "D" | "G") => {
    return observations.find((o) => o.zone_id === zoneId && o.eye_side === eyeSide);
  };

  // Handler pour ajouter une zone depuis IrisSvgMapV2
  const handleAddZoneFromMapV2 = useCallback((zone: ZoneDataV2, eyeSide: "D" | "G") => {
    const irisZone: IrisZone = {
      id: zone.id,
      label: zone.label || `Zone ${zone.zone_num}`,
      ring: "organic" as const,
      startAngle: 0,
      endAngle: 360,
      priority: 1,
      color: "#4CAF50",
      subZones: [],
      description_iridologie: zone.lecture_iridologique || "",
      pistes_action: zone.pistes_accompagnement || [],
      signaux_alerte: zone.points_vigilance || [],
    };

    const exists = selectedZonesForExport.some(
      (z) => z.zone.id === zone.id && z.eyeSide === eyeSide
    );
    
    if (exists) {
      setSelectedZonesForExport(
        selectedZonesForExport.filter((z) => !(z.zone.id === zone.id && z.eyeSide === eyeSide))
      );
      toast.info("Zone retirée de la sélection");
    } else {
      setSelectedZonesForExport([...selectedZonesForExport, { zone: irisZone, eyeSide, severityLevel: "normal" }]);
      toast.success("Zone ajoutée au compte-rendu");
    }
  }, [selectedZonesForExport, setSelectedZonesForExport]);

  const isZoneInSelectionById = useCallback((zoneId: string) => {
    return selectedZonesForExport.some((z) => z.zone.id === zoneId);
  }, [selectedZonesForExport]);

  return (
    <div data-iridology-comparison className={`space-y-4 ${className}`}>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Columns2 className="w-5 h-5 text-primary" />
              Comparaison des deux yeux
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 p-3 sm:p-6">
          {/* Carte iridologique interactive avec les nouveaux SVG/JSON */}
          <div className="space-y-3 sm:space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-[10px] sm:text-xs font-normal">
                  <Map className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
                  Carte iridologique
                </Badge>
                <Badge variant="secondary" className="text-[10px] sm:text-xs">
                  Zones colorées
                </Badge>
              </div>
              <div className="sm:ml-auto flex items-center gap-2">
                <Tabs value={selectedEye ?? "both"} onValueChange={(v) => setSelectedEye((v === "both" ? null : (v as "D" | "G")))}>
                  <TabsList className="h-7 sm:h-8">
                    <TabsTrigger value="both" className="text-[10px] sm:text-xs px-2 sm:px-3 h-6 sm:h-7">
                      <Columns2 className="h-3 w-3 sm:mr-1" />
                      <span className="hidden sm:inline">Deux yeux</span>
                    </TabsTrigger>
                    <TabsTrigger value="D" className="text-[10px] sm:text-xs px-2 sm:px-3 h-6 sm:h-7">
                      <Eye className="h-3 w-3 sm:mr-1" />
                      <span className="hidden sm:inline">Œil droit</span>
                      <span className="sm:hidden">D</span>
                    </TabsTrigger>
                    <TabsTrigger value="G" className="text-[10px] sm:text-xs px-2 sm:px-3 h-6 sm:h-7">
                      <Eye className="h-3 w-3 sm:mr-1" />
                      <span className="hidden sm:inline">Œil gauche</span>
                      <span className="sm:hidden">G</span>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>

            {/* Les deux cartes SVG : toujours montées (zones cliquables), le bouton masquer/afficher est DANS IrisSvgMapV2 */}
            <div className={selectedEye ? "grid grid-cols-1" : "grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6"}>
              {/* Important: always keep both eyes mounted so calibration stays stable when switching tabs */}
              <div className={selectedEye === "G" ? "hidden" : ""}>
                <IrisSvgMapV2
                  eyeSide="droit"
                  sessionId={sessionId}
                  irisPhotoUrl={rightEye.imageData || undefined}
                  onAddZone={(zone) => handleAddZoneFromMapV2(zone, "D")}
                  isZoneInSelection={isZoneInSelectionById}
                  showMap={showCartographyRight}
                  onToggleMap={() => setShowCartographyRight((v) => !v)}
                />
              </div>

              <div className={selectedEye === "D" ? "hidden" : ""}>
                <IrisSvgMapV2
                  eyeSide="gauche"
                  sessionId={sessionId}
                  irisPhotoUrl={leftEye.imageData || undefined}
                  onAddZone={(zone) => handleAddZoneFromMapV2(zone, "G")}
                  isZoneInSelection={isZoneInSelectionById}
                  showMap={showCartographyLeft}
                  onToggleMap={() => setShowCartographyLeft((v) => !v)}
                />
              </div>
            </div>
          </div>


          {showObservationForm && selectedZone && selectedEye && (
            <div className="mt-4">
              <ZoneObservationForm
                zone={selectedZone}
                eyeSide={selectedEye}
                sessionId={sessionId}
                existingObservation={getExistingObservation(selectedZone.id, selectedEye)}
                onSave={handleObservationSaved}
                onCancel={() => setShowObservationForm(false)}
              />
            </div>
          )}

          {/* Selected zones panel */}
          {selectedZonesForExport.length > 0 && (
            <SelectedZonesPanel
              selectedZones={selectedZonesForExport}
              onRemoveZone={handleRemoveZoneFromSelection}
              onUpdateZone={handleUpdateZone}
              onClearAll={handleClearAllSelection}
              onExportPdf={() => {}} // Will be handled by parent
              isExporting={isExporting}
            />
          )}

          {observations.length > 0 && (
            <div className="mt-4 p-3 bg-muted/30 rounded-lg">
              <div className="text-sm font-medium mb-2">
                Observations enregistrées ({observations.length})
              </div>
              <div className="flex flex-wrap gap-1">
                {observations.map((obs) => (
                  <Badge
                    key={obs.id}
                    variant={obs.severity_level === "priority" ? "destructive" : obs.severity_level === "attention" ? "secondary" : "outline"}
                    className="text-xs"
                  >
                    {obs.eye_side === "D" ? "OD" : "OG"} - {obs.zone_label}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Alert variant="default" className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-sm text-amber-800 dark:text-amber-200">
          La comparaison bilatérale permet d'identifier les asymétries et d'affiner l'analyse du terrain.
        </AlertDescription>
      </Alert>
    </div>
  );
};
